import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useJams, useCreateJam, useDeleteJam, useJoinJam } from "@/hooks/api/useJams";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useConfirm } from "@/hooks/useConfirm";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { Jam } from "@/types/jams";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Never";
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "No activity";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

function JamCard({
  jam,
  onDelete,
  isDeleting,
}: {
  jam: Jam;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  const { toast } = useToast();

  const copyToClipboard = async (text: string, title: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title });
    } catch {
      toast({ title: "Failed to copy" });
    }
  };

  const inviteLink = `${window.location.origin}/join/${jam.joinCode}`;

  return (
    <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <Link
            to={`/jams/${jam.id}`}
            className="text-base font-medium hover:text-primary transition-colors"
          >
            {jam.name}
          </Link>
          {jam.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {jam.description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onDelete(jam.id)}
          disabled={isDeleting}
        >
          Delete
        </Button>
      </div>

      {/* Invite code display */}
      <div className="mt-4 p-3 bg-[#1e1e1e] rounded-md">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Invite code
            </p>
            <code className="text-base font-mono tracking-[0.25em]">
              {jam.joinCode}
            </code>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                copyToClipboard(jam.joinCode, "Invite code copied to clipboard")
              }
            >
              Copy Code
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                copyToClipboard(inviteLink, "Invite link copied to clipboard")
              }
            >
              Copy Link
            </Button>
          </div>
        </div>
      </div>

      {/* Meta info */}
      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground font-mono">
        <span>Created {formatDate(jam.createdAt)}</span>
        <span>Last activity: {formatRelativeTime(jam.lastActivityAt)}</span>
        {jam.discardedAt && (
          <span className="text-destructive font-medium font-sans">Deleted</span>
        )}
      </div>
    </div>
  );
}

export default function Jams() {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: jams, isLoading } = useJams();
  const createJam = useCreateJam();
  const deleteJam = useDeleteJam();
  const joinJam = useJoinJam();
  const { confirmProps, confirm } = useConfirm();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [newJamName, setNewJamName] = useState("");
  const [newJamDescription, setNewJamDescription] = useState("");
  const [joinCode, setJoinCode] = useState("");

  const handleJoinJam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!joinCode.trim()) return;

    joinJam.mutate(joinCode.trim(), {
      onSuccess: (result) => {
        setJoinCode("");
        setShowJoinForm(false);
        toast({ title: `Joined "${result.data.jam.name}"` });
        navigate(`/jams/${result.data.jam.id}`);
      },
    });
  };

  const handleCreateJam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newJamName.trim()) return;

    createJam.mutate(
      {
        name: newJamName.trim(),
        description: newJamDescription.trim() || undefined,
      },
      {
        onSuccess: () => {
          setNewJamName("");
          setNewJamDescription("");
          setShowCreateForm(false);
        },
      }
    );
  };

  const handleDeleteJam = (jamId: string) => {
    confirm({
      title: "Delete Jam",
      description: "Are you sure you want to delete this jam?",
      confirmLabel: "Delete",
      variant: "destructive",
      onConfirm: () => deleteJam.mutate(jamId),
    });
  };

  // Separate active and deleted jams for admin view
  const activeJams = jams?.filter((j) => !j.discardedAt) || [];
  const deletedJams = jams?.filter((j) => j.discardedAt) || [];

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-xl font-medium uppercase tracking-wider">
          {isAdmin ? "All Jams" : "My Jams"}
        </h1>
        <div className="flex gap-2">
          {!showJoinForm && (
            <Button variant="outline" onClick={() => setShowJoinForm(true)}>
              Join Jam
            </Button>
          )}
          {!showCreateForm && (
            <Button onClick={() => setShowCreateForm(true)}>Create Jam</Button>
          )}
        </div>
      </div>

      {/* Join jam form */}
      {showJoinForm && (
        <form
          onSubmit={handleJoinJam}
          className="bg-[#191919] border border-[#616161]/20 rounded-lg p-4 mb-8"
        >
          <h2 className="text-sm font-medium uppercase tracking-wider mb-4">Join a Jam</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="joinCode">Invite code</Label>
              <Input
                id="joinCode"
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="e.g. H7KQ4F"
                autoFocus
                required
                maxLength={12}
                className="font-mono uppercase"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={joinJam.isPending}>
                {joinJam.isPending ? "Joining..." : "Join"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowJoinForm(false);
                  setJoinCode("");
                }}
              >
                Cancel
              </Button>
            </div>
            {joinJam.isError && (
              <p className="text-sm text-destructive">
                {joinJam.error?.message || "Failed to join jam"}
              </p>
            )}
          </div>
        </form>
      )}

      {/* Create jam form */}
      {showCreateForm && (
        <form
          onSubmit={handleCreateJam}
          className="bg-[#191919] border border-[#616161]/20 rounded-lg p-4 mb-8"
        >
          <h2 className="text-sm font-medium uppercase tracking-wider mb-4">Create New Jam</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="jamName">Name *</Label>
              <Input
                id="jamName"
                type="text"
                value={newJamName}
                onChange={(e) => setNewJamName(e.target.value)}
                placeholder="My Awesome Jam"
                required
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="jamDescription">Description (optional)</Label>
              <Textarea
                id="jamDescription"
                value={newJamDescription}
                onChange={(e) => setNewJamDescription(e.target.value)}
                placeholder="What's this jam about?"
                maxLength={500}
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={createJam.isPending}>
                {createJam.isPending ? "Creating..." : "Create"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewJamName("");
                  setNewJamDescription("");
                }}
              >
                Cancel
              </Button>
            </div>
            {createJam.isError && (
              <p className="text-sm text-destructive">
                {createJam.error?.message || "Failed to create jam"}
              </p>
            )}
          </div>
        </form>
      )}

      {/* Jams list */}
      {isLoading ? (
        <p className="text-muted-foreground">Loading jams...</p>
      ) : activeJams.length > 0 ? (
        <div className="space-y-4">
          {activeJams.map((jam) => (
            <JamCard
              key={jam.id}
              jam={jam}
              onDelete={handleDeleteJam}
              isDeleting={deleteJam.isPending}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-[#191919] border border-[#616161]/20 rounded-lg">
          <p className="text-muted-foreground mb-4">No jams yet.</p>
          {!showCreateForm && (
            <Button onClick={() => setShowCreateForm(true)}>
              Create Your First Jam
            </Button>
          )}
        </div>
      )}

      {/* Deleted jams (admin only) */}
      {isAdmin && deletedJams.length > 0 && (
        <div className="mt-12">
          <h2 className="text-sm font-medium uppercase tracking-wider mb-4 text-muted-foreground">
            Deleted Jams
          </h2>
          <div className="space-y-4 opacity-60">
            {deletedJams.map((jam) => (
              <JamCard
                key={jam.id}
                jam={jam}
                onDelete={handleDeleteJam}
                isDeleting={deleteJam.isPending}
              />
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
